import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Icon } from "@iconify/react";
import { ModernInput } from "../../../components/common";
import SearchBadgeGroup from "../../../components/molecules/SearchBadgeGroup";
import { useTranslation } from "../../../i18n/I18nContext";
import { useLocalePath } from "../../../hooks/useLocalePath";
import { LANDING_MAX, LANDING_PAD_X_BLEED } from "./landingLayout";
import "./LandingHero.css";

export function LandingHero() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const lp = useLocalePath();
  const [query, setQuery] = useState("");
  const [city, setCity] = useState("");
  const badges = [
    t("categories.beautySpa"),
    t("categories.healthcare"),
    t("categories.homeServices"),
    t("categories.fitness"),
  ];

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (query.trim()) params.set("q", query.trim());
    if (city.trim()) params.set("city", city.trim());
    navigate(lp(`/explore${params.toString() ? `?${params.toString()}` : ""}`));
  };

  return (
    <section id="hero" aria-labelledby="hero-title" className="landing-hero-root flex flex-col items-stretch">
      <div className="landing-hero-gradient" aria-hidden />
      <div className="landing-hero-visuals" aria-hidden>
        <div className="landing-hero-photo-slot">
          <img
            src="/images/hero-bg.webp"
            alt=""
            aria-hidden
            width="1440"
            height="897"
            fetchPriority="high"
            decoding="async"
            className="landing-hero-photo"
          />
          <div className="landing-hero-photo-overlay" />
        </div>
        <div className="landing-hero-bloom landing-hero-bloom--1 hidden lg:block" />
        <div className="landing-hero-bloom landing-hero-bloom--2 landing-hero-bloom--mirror hidden lg:block" />
        <div className="landing-hero-bloom landing-hero-bloom--3 hidden lg:block" />
        <div className="landing-hero-bloom landing-hero-bloom--4 landing-hero-bloom--mirror hidden lg:block" />
      </div>

      <div className={`relative z-[2] ${LANDING_MAX} ${LANDING_PAD_X_BLEED} pb-12 pt-16 sm:pb-16 sm:pt-20 lg:pb-[100px] lg:pt-[140px]`}>
        <div className="flex w-full flex-col items-center gap-8 sm:gap-10 lg:gap-[45px]">
          <div className="flex w-full flex-col gap-5 text-center text-white sm:gap-6 lg:gap-[25px]">
            <h1
              id="hero-title"
              className="font-bold leading-[130%] text-[32px] text-white sm:text-[40px] md:text-[48px] lg:text-[56px] xl:text-[64px]"
            >
              {t("hero.title")}
            </h1>
            <p className="w-full font-medium leading-[32px] text-base text-white sm:text-lg lg:text-[22px]">
              {t("hero.subtext")}
            </p>
          </div>

          <div className="flex w-full flex-col items-center gap-8 sm:gap-10 lg:gap-[45px]">
            <form
              className="box-border w-full rounded-[20px] border border-[rgba(255,255,255,0.5)] bg-[rgba(255,255,255,0.2)] p-5 sm:p-[25px]"
              onSubmit={handleSearch}
            >
              <div className="flex w-full flex-col gap-[17px]">
                <ModernInput
                  variant="hero-glass"
                  placeholder={t("hero.searchPlaceholder")}
                  icon="mdi:magnify"
                  containerClassName="w-full"
                  value={query}
                  onChange={setQuery}
                />
                <ModernInput
                  variant="hero-glass"
                  placeholder={t("hero.locationPlaceholder")}
                  icon="mdi:map-marker-outline"
                  containerClassName="w-full"
                  value={city}
                  onChange={setCity}
                />
                <button
                  type="submit"
                  className="flex h-[60px] w-full shrink-0 items-center justify-center gap-[13px] rounded-[15px] bg-white px-7 font-medium text-[#253477] text-xl leading-9 lg:text-2xl lg:leading-9"
                >
                  <Icon icon="mdi:magnify" className="h-[17px] w-[17px] shrink-0 text-[#253477] sm:h-6 sm:w-6 lg:h-[17px] lg:w-[17px]" aria-hidden />
                  {t("hero.search")}
                </button>
                <SearchBadgeGroup badges={badges} />
              </div>
            </form>

            <Link
              to={lp("/register")}
              className="inline-flex h-[52px] w-full max-w-[244px] items-center justify-center gap-2.5 rounded-[15px] bg-white px-[19px] py-2 font-semibold text-[#253477] text-[18px] leading-5 tracking-[-0.15px]"
            >
              {t("hero.getStartedFree")}
              <Icon icon="mdi:chevron-right" className="h-4 w-4 shrink-0 text-[#403A3A]" aria-hidden />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
